import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Input, Button, Card, Typography, Space, Row, Col } from "antd";
import { SendOutlined } from "@ant-design/icons";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";
import SearchHistory from "../common/SearchHistory";
import { useSearchHistory } from "../../hooks/useSearchHistory";

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

function Chat() {
  const [prompt, setPrompt] = useState("");
  const [chatResponse, setChatResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { logout } = useAuth();
  const { history, addToHistory, removeFromHistory, clearHistory } = useSearchHistory('chat');

  const askAI = async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt before asking AI");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem("token");

      if (!token) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }

      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${apiUrl}/api/ask-ai?prompt=${encodeURIComponent(prompt)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401 || response.status === 403) {
        setError("Your session has expired. Please log in again.");
        logout();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.text();
      setChatResponse(data);
      addToHistory({ prompt, response: data });
    } catch (error) {
      console.error("Error asking AI:", error);
      setError(error.message || "An error occurred while communicating with AI");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askAI();
    }
  };

  const clearChat = () => {
    setPrompt("");
    setChatResponse("");
    setError(null);
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '24px', color: '#1890ff' }}>
        🤖 Talk to AI
      </Title>
      
      {error && (
        <ErrorMessage 
          message="Error" 
          description={error} 
          type="error" 
          closable={true}
        />
      )}

      <SearchHistory
        history={history}
        onUsePrompt={(promptText) => setPrompt(promptText)}
        onRemovePrompt={removeFromHistory}
        onClearHistory={clearHistory}
        title="Chat History"
        emptyText="No chat history yet. Your conversations will appear here."
      />

      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter your question or prompt for AI..."
            rows={4}
            style={{ 
              fontSize: '16px',
              borderRadius: '8px',
              borderColor: '#d9d9d9'
            }}
            autoFocus
          />
          <Row gutter={16} justify="space-between">
            <Col>
              <Button 
                onClick={clearChat} 
                style={{ marginRight: '8px' }}
              >
                Clear
              </Button>
            </Col>
            <Col>
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                onClick={askAI}
                loading={loading}
                disabled={loading}
                style={{ 
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff'
                }}
              >
                {loading ? 'Asking...' : 'Ask AI'}
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {loading && <LoadingSpinner tip="AI is thinking..." />}

      {chatResponse && !loading && (
        <Card 
          title="AI Response" 
          style={{ 
            backgroundColor: '#fafafa',
            border: '1px solid #f0f0f0'
          }}
        >
          <Paragraph style={{ fontSize: '16px', lineHeight: '1.6' }}>
            {chatResponse}
          </Paragraph>
        </Card>
      )}

      {!chatResponse && !loading && (
        <Card style={{ textAlign: 'center', color: '#8c8c8c' }}>
          <Paragraph>
            Enter a prompt above and click "Ask AI" to get started. 
            <br />
            Try asking about technology, recipes, or anything you're curious about!
          </Paragraph>
        </Card>
      )}
    </div>
  );
}

export default Chat;
